import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
    Bike, Plus, CheckCircle, XCircle, Clock, ChefHat, Package,
    ArrowRight, TrendingUp, DollarSign, ShoppingBag, Phone, Globe,
    DoorOpen, Car, UtensilsCrossed, Search, Filter
} from 'lucide-react';

const DeliveryHub = () => {
    const { user } = useAuth();
    const [orders, setOrders] = useState([]);
    const [platforms, setPlatforms] = useState([]);
    const [settings, setSettings] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('orders');
    const [filterStatus, setFilterStatus] = useState('all');
    const [showNewOrderModal, setShowNewOrderModal] = useState(false);

    // New order form state
    const [newOrder, setNewOrder] = useState({
        platform: 'phone',
        external_order_id: '',
        customer_name: '',
        customer_phone: '',
        customer_address: '',
        items: [{ sku: '', name: '', quantity: 1, unit_price: 0 }],
        subtotal: 0,
        tax: 0,
        delivery_fee: 0,
        platform_fee: 0,
        total: 0,
        special_instructions: ''
    });

    const loadData = useCallback(async () => {
        try {
            setLoading(true);
            const storeId = user?.store_id;
            const [ordersRes, platformsRes, settingsRes] = await Promise.all([
                api.get('/delivery/orders', { params: { limit: 50 } }),
                api.get('/delivery/platforms'),
                api.get('/delivery/settings', { params: { store_id: storeId } })
            ]);
            setOrders(ordersRes.data || []);
            setPlatforms(platformsRes.data?.platforms || []);
            setSettings(settingsRes.data);
        } catch (error) {
            console.error('Failed to load delivery data:', error);
            toast.error('Failed to load delivery data');
        } finally {
            setLoading(false);
        }
    }, [user]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const handleAcceptOrder = async (orderId) => {
        try {
            await api.post(`/delivery/orders/${orderId}/accept`);
            toast.success('Order accepted');
            loadData();
        } catch (error) {
            toast.error('Failed to accept order');
        }
    };

    const handleRejectOrder = async (orderId) => {
        const reason = prompt('Enter rejection reason:');
        if (!reason) return;
        try {
            await api.post(`/delivery/orders/${orderId}/reject`, { reason });
            toast.success('Order rejected');
            loadData();
        } catch (error) {
            toast.error('Failed to reject order');
        }
    };

    const handleUpdateStatus = async (orderId, status) => {
        try {
            await api.post(`/delivery/orders/${orderId}/status`, { status });
            toast.success(`Order status updated to ${status}`);
            loadData();
        } catch (error) {
            toast.error('Failed to update status');
        }
    };

    const handleEnablePlatform = async (platformId, enable) => {
        try {
            const endpoint = enable ? 'enable' : 'disable';
            await api.post(`/delivery/platforms/${platformId}/${endpoint}`);
            toast.success(`${platformId} ${enable ? 'enabled' : 'disabled'}`);
            loadData();
        } catch (error) {
            toast.error(`Failed to ${enable ? 'enable' : 'disable'} platform`);
        }
    };

    const handleSaveSettings = async () => {
        try {
            const settingsData = {
                store_id: user?.store_id,
                auto_accept_enabled: !settings?.auto_accept_enabled,
                auto_accept_max_order_value: parseFloat(document.getElementById('max-order-value')?.value) || 500,
                auto_accept_min_margin: parseFloat(document.getElementById('min-margin')?.value) || 10
            };
            await api.put('/delivery/settings', settingsData);
            toast.success('Settings saved successfully');
            loadData();
        } catch (error) {
            toast.error('Failed to save settings');
        }
    };

    const calculateTotals = () => {
        const subtotal = newOrder.items.reduce((sum, item) => 
            sum + (item.quantity * item.unit_price), 0
        );
        const tax = subtotal * 0.08;
        const total = subtotal + tax + newOrder.delivery_fee + newOrder.platform_fee;
        setNewOrder(prev => ({ ...prev, subtotal, tax, total }));
    };

    const addItem = () => {
        setNewOrder(prev => ({
            ...prev,
            items: [...prev.items, { sku: '', name: '', quantity: 1, unit_price: 0 }]
        }));
    };

    const updateItem = (index, field, value) => {
        const updatedItems = [...newOrder.items];
        updatedItems[index][field] = field === 'quantity' || field === 'unit_price' ? parseFloat(value) || 0 : value;
        setNewOrder(prev => ({ ...prev, items: updatedItems }));
        calculateTotals();
    };

    const removeItem = (index) => {
        setNewOrder(prev => ({
            ...prev,
            items: prev.items.filter((_, i) => i !== index)
        }));
    };

    const handleCreateOrder = async () => {
        try {
            await api.post('/delivery/orders/manual', newOrder);
            toast.success('Order created successfully');
            setShowNewOrderModal(false);
            loadData();
        } catch (error) {
            toast.error('Failed to create order');
        }
    };

    const getStatusBadge = (status) => {
        const configs = {
            new: { bg: 'bg-amber-500/15', text: 'text-amber-400', icon: Clock },
            accepted: { bg: 'bg-blue-500/15', text: 'text-blue-400', icon: CheckCircle },
            preparing: { bg: 'bg-orange-500/15', text: 'text-orange-400', icon: ChefHat },
            ready: { bg: 'bg-purple-500/15', text: 'text-purple-400', icon: Package },
            out_for_delivery: { bg: 'bg-indigo-500/15', text: 'text-indigo-400', icon: Bike },
            completed: { bg: 'bg-emerald-500/15', text: 'text-emerald-400', icon: CheckCircle },
            cancelled: { bg: 'bg-red-500/15', text: 'text-red-400', icon: XCircle },
            rejected: { bg: 'bg-slate-500/15', text: 'text-slate-400', icon: XCircle }
        };
        const config = configs[status] || configs.new;
        const Icon = config.icon;
        return (
            <Badge className={`${config.bg} ${config.text} border-0 capitalize gap-1`}>
                <Icon className="h-3 w-3" />
                {status.replace('_', ' ')}
            </Badge>
        );
    };

    const getPlatformIcon = (platform) => {
        const icons = {
            doordash: <DoorOpen className="h-4 w-4" />,
            uber_eats: <Car className="h-4 w-4" />,
            grubhub: <UtensilsCrossed className="h-4 w-4" />,
            instacart: <ShoppingBag className="h-4 w-4" />,
            phone: <Phone className="h-4 w-4" />,
            website: <Globe className="h-4 w-4" />
        };
        return icons[platform] || <ShoppingBag className="h-4 w-4" />;
    };

    const filteredOrders = filterStatus === 'all' 
        ? orders 
        : orders.filter(o => o.status === filterStatus);

    const statCards = [
        { 
            label: 'New Orders', 
            value: orders.filter(o => o.status === 'new').length,
            icon: Clock,
            accent: 'border-amber-500',
            iconBg: 'bg-amber-500/10',
            iconColor: 'text-amber-400'
        },
        { 
            label: 'Preparing', 
            value: orders.filter(o => ['accepted', 'preparing'].includes(o.status)).length,
            icon: ChefHat,
            accent: 'border-orange-500',
            iconBg: 'bg-orange-500/10',
            iconColor: 'text-orange-400'
        },
        { 
            label: 'Ready', 
            value: orders.filter(o => o.status === 'ready').length,
            icon: Package,
            accent: 'border-purple-500',
            iconBg: 'bg-purple-500/10',
            iconColor: 'text-purple-400'
        },
        { 
            label: 'Today\'s Revenue', 
            value: `$${orders
                .filter(o => ['completed', 'accepted', 'preparing', 'ready'].includes(o.status))
                .reduce((sum, o) => sum + (o.total || 0), 0)
                .toFixed(2)}`,
            icon: DollarSign,
            accent: 'border-emerald-500',
            iconBg: 'bg-emerald-500/10',
            iconColor: 'text-emerald-400'
        }
    ];

    return (
        <Layout>
            <div className="space-y-6">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                        <h1 className="text-2xl font-bold tracking-tight">Delivery Hub</h1>
                        <p className="text-sm text-muted-foreground">Manage online orders from all platforms</p>
                    </div>
                    <Button onClick={() => setShowNewOrderModal(true)} className="gap-2">
                        <Plus className="h-4 w-4" />
                        New Order
                    </Button>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    {statCards.map((card) => {
                        const Icon = card.icon;
                        return (
                            <Card key={card.label} className="stat-card border-l-4 ${card.accent}">
                                <CardContent className="p-4">
                                    <div className="flex items-start justify-between mb-3">
                                        <div className={`stat-card-icon ${card.iconBg}`}>
                                            <Icon className={`h-5 w-5 ${card.iconColor}`} />
                                        </div>
                                        <TrendingUp className="h-4 w-4 text-muted-foreground" />
                                    </div>
                                    <div className="text-3xl font-black font-mono tracking-tight">{card.value}</div>
                                    <p className="text-xs text-muted-foreground mt-1 font-medium">{card.label}</p>
                                </CardContent>
                            </Card>
                        );
                    })}
                </div>

                {/* Tabs */}
                <div className="flex gap-2 border-b border-border">
                    {['orders', 'platforms', 'settings'].map((tab) => (
                        <button
                            key={tab}
                            onClick={() => setActiveTab(tab)}
                            className={`px-4 py-2 text-sm font-medium capitalize transition-colors relative ${
                                activeTab === tab 
                                    ? 'text-foreground' 
                                    : 'text-muted-foreground hover:text-foreground'
                            }`}
                        >
                            {tab}
                            {activeTab === tab && (
                                <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-cyan-500" />
                            )}
                        </button>
                    ))}
                </div>

                {/* Orders Tab */}
                {activeTab === 'orders' && (
                    <div className="space-y-4">
                        {/* Filters */}
                        <div className="flex flex-wrap gap-2">
                            {['all', 'new', 'accepted', 'preparing', 'ready', 'completed', 'cancelled'].map(status => (
                                <Button
                                    key={status}
                                    variant={filterStatus === status ? 'default' : 'outline'}
                                    size="sm"
                                    onClick={() => setFilterStatus(status)}
                                    className="capitalize"
                                >
                                    {status.replace('_', ' ')}
                                </Button>
                            ))}
                        </div>

                        {/* Orders Table */}
                        <Card className="shadow-sm">
                            <CardHeader className="pb-3">
                                <div className="flex items-center justify-between">
                                    <CardTitle className="flex items-center gap-2 text-base">
                                        <Bike className="h-4 w-4 text-cyan-400" />
                                        Orders
                                    </CardTitle>
                                    <Badge variant="outline">{filteredOrders.length} total</Badge>
                                </div>
                            </CardHeader>
                            <CardContent className="p-0">
                                {filteredOrders.length === 0 ? (
                                    <div className="py-12 text-center text-sm text-muted-foreground">
                                        No orders found
                                    </div>
                                ) : (
                                    <div className="divide-y divide-border">
                                        {filteredOrders.map(order => (
                                            <div key={order.id} className="flex items-center gap-4 px-5 py-4 hover:bg-muted/30 transition-colors">
                                                {/* Platform Icon */}
                                                <div className="h-10 w-10 rounded-lg bg-muted flex items-center justify-center shrink-0">
                                                    {getPlatformIcon(order.platform)}
                                                </div>
                                                
                                                {/* Order Info */}
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-2">
                                                        <p className="font-semibold text-sm">{order.external_order_id}</p>
                                                        {getStatusBadge(order.status)}
                                                    </div>
                                                    <p className="text-xs text-muted-foreground truncate">
                                                        {order.customer_name || 'No customer name'} • {order.items?.length || 0} items
                                                    </p>
                                                </div>

                                                {/* Total */}
                                                <div className="text-right shrink-0">
                                                    <p className="font-mono font-semibold">${order.total?.toFixed(2)}</p>
                                                    <p className="text-xs text-muted-foreground capitalize">{order.platform}</p>
                                                </div>

                                                {/* Actions */}
                                                <div className="flex gap-2 shrink-0">
                                                    {order.status === 'new' && (
                                                        <>
                                                            <Button 
                                                                size="sm" 
                                                                variant="outline"
                                                                className="border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10"
                                                                onClick={() => handleAcceptOrder(order.id)}
                                                            >
                                                                Accept
                                                            </Button>
                                                            <Button 
                                                                size="sm" 
                                                                variant="outline"
                                                                className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                                                                onClick={() => handleRejectOrder(order.id)}
                                                            >
                                                                Reject
                                                            </Button>
                                                        </>
                                                    )}
                                                    {order.status === 'accepted' && (
                                                        <Button 
                                                            size="sm" 
                                                            variant="outline"
                                                            onClick={() => handleUpdateStatus(order.id, 'preparing')}
                                                        >
                                                            Start Prep
                                                        </Button>
                                                    )}
                                                    {order.status === 'preparing' && (
                                                        <Button 
                                                            size="sm" 
                                                            variant="outline"
                                                            onClick={() => handleUpdateStatus(order.id, 'ready')}
                                                        >
                                                            Mark Ready
                                                        </Button>
                                                    )}
                                                    {order.status === 'ready' && (
                                                        <Button 
                                                            size="sm" 
                                                            variant="outline"
                                                            className="border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10"
                                                            onClick={() => handleUpdateStatus(order.id, 'completed')}
                                                        >
                                                            Complete
                                                        </Button>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </div>
                )}

                {/* Platforms Tab */}
                {activeTab === 'platforms' && (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {platforms.map(platform => (
                            <Card key={platform.id} className="shadow-sm">
                                <CardContent className="p-5">
                                    <div className="flex items-start justify-between mb-4">
                                        <div className="flex items-center gap-3">
                                            <div className="h-10 w-10 rounded-lg bg-muted flex items-center justify-center">
                                                {getPlatformIcon(platform.id)}
                                            </div>
                                            <div>
                                                <h3 className="font-semibold">{platform.name}</h3>
                                                {platform.exclusive_partner && (
                                                    <Badge className="bg-emerald-500/15 text-emerald-400 border-0 text-xs">
                                                        Exclusive
                                                    </Badge>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <p className="text-sm text-muted-foreground mb-4">{platform.description}</p>
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm text-muted-foreground">
                                            Commission: {platform.commission_rate}%
                                        </span>
                                        <Button 
                                            size="sm" 
                                            variant={settings?.platforms?.[platform.id]?.enabled ? 'default' : 'outline'}
                                            onClick={() => handleEnablePlatform(platform.id, !settings?.platforms?.[platform.id]?.enabled)}
                                        >
                                            {settings?.platforms?.[platform.id]?.enabled ? 'Enabled' : 'Enable'}
                                        </Button>
                                    </div>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                )}

                {/* Settings Tab */}
                {activeTab === 'settings' && (
                    <Card className="shadow-sm">
                        <CardHeader>
                            <CardTitle className="text-base">Auto-Accept Settings</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <div className="font-medium">Auto-accept orders</div>
                                    <div className="text-sm text-muted-foreground">
                                        Automatically accept orders within parameters
                                    </div>
                                </div>
                                <div 
                                    className={`w-12 h-6 rounded-full p-1 cursor-pointer transition-colors ${
                                        settings?.auto_accept_enabled ? 'bg-cyan-600' : 'bg-muted'
                                    }`}
                                    onClick={handleSaveSettings}
                                >
                                    <div className={`w-4 h-4 bg-white rounded-full transition-transform ${
                                        settings?.auto_accept_enabled ? 'translate-x-6' : ''
                                    }`} />
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <label className="text-sm font-medium">Max Order Value ($)</label>
                                    <input
                                        id="max-order-value"
                                        type="number"
                                        defaultValue={settings?.auto_accept_max_order_value || 500}
                                        className="w-full bg-background border border-input rounded-lg px-3 py-2"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-sm font-medium">Min Margin (%)</label>
                                    <input
                                        id="min-margin"
                                        type="number"
                                        defaultValue={settings?.auto_accept_min_margin || 10}
                                        className="w-full bg-background border border-input rounded-lg px-3 py-2"
                                    />
                                </div>
                            </div>
                            <Button onClick={handleSaveSettings} className="w-full">
                                Save Settings
                            </Button>
                        </CardContent>
                    </Card>
                )}
            </div>

            {/* New Order Modal */}
            {showNewOrderModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
                    <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                        <CardHeader>
                            <CardTitle>Create New Order</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            {/* Platform & Order ID */}
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <label className="text-sm font-medium">Platform</label>
                                    <select
                                        value={newOrder.platform}
                                        onChange={(e) => setNewOrder({...newOrder, platform: e.target.value})}
                                        className="w-full bg-background border border-input rounded-lg px-3 py-2"
                                    >
                                        <option value="phone">Phone</option>
                                        <option value="website">Website</option>
                                        <option value="doordash">DoorDash</option>
                                        <option value="uber_eats">UberEats</option>
                                        <option value="grubhub">Grubhub</option>
                                    </select>
                                </div>
                                <div className="space-y-2">
                                    <label className="text-sm font-medium">Order ID</label>
                                    <input
                                        type="text"
                                        value={newOrder.external_order_id}
                                        onChange={(e) => setNewOrder({...newOrder, external_order_id: e.target.value})}
                                        placeholder="e.g., PH-12345"
                                        className="w-full bg-background border border-input rounded-lg px-3 py-2"
                                    />
                                </div>
                            </div>

                            {/* Customer Info */}
                            <div className="space-y-2">
                                <label className="text-sm font-medium">Customer Information</label>
                                <div className="grid grid-cols-2 gap-2">
                                    <input
                                        type="text"
                                        placeholder="Customer Name"
                                        value={newOrder.customer_name}
                                        onChange={(e) => setNewOrder({...newOrder, customer_name: e.target.value})}
                                        className="bg-background border border-input rounded-lg px-3 py-2"
                                    />
                                    <input
                                        type="text"
                                        placeholder="Phone Number"
                                        value={newOrder.customer_phone}
                                        onChange={(e) => setNewOrder({...newOrder, customer_phone: e.target.value})}
                                        className="bg-background border border-input rounded-lg px-3 py-2"
                                    />
                                </div>
                                <input
                                    type="text"
                                    placeholder="Delivery Address"
                                    value={newOrder.customer_address}
                                    onChange={(e) => setNewOrder({...newOrder, customer_address: e.target.value})}
                                    className="w-full bg-background border border-input rounded-lg px-3 py-2"
                                />
                            </div>

                            {/* Items */}
                            <div className="space-y-2">
                                <div className="flex items-center justify-between">
                                    <label className="text-sm font-medium">Items</label>
                                    <Button type="button" variant="outline" size="sm" onClick={addItem}>
                                        <Plus className="h-4 w-4 mr-1" />
                                        Add Item
                                    </Button>
                                </div>
                                {newOrder.items.map((item, index) => (
                                    <div key={index} className="flex gap-2">
                                        <input
                                            type="text"
                                            placeholder="SKU"
                                            value={item.sku}
                                            onChange={(e) => updateItem(index, 'sku', e.target.value)}
                                            className="flex-1 bg-background border border-input rounded-lg px-3 py-2"
                                        />
                                        <input
                                            type="text"
                                            placeholder="Item Name"
                                            value={item.name}
                                            onChange={(e) => updateItem(index, 'name', e.target.value)}
                                            className="flex-[2] bg-background border border-input rounded-lg px-3 py-2"
                                        />
                                        <input
                                            type="number"
                                            placeholder="Qty"
                                            value={item.quantity}
                                            onChange={(e) => updateItem(index, 'quantity', e.target.value)}
                                            className="w-20 bg-background border border-input rounded-lg px-3 py-2"
                                        />
                                        <input
                                            type="number"
                                            placeholder="Price"
                                            value={item.unit_price}
                                            onChange={(e) => updateItem(index, 'unit_price', e.target.value)}
                                            className="w-24 bg-background border border-input rounded-lg px-3 py-2"
                                        />
                                        <Button 
                                            type="button" 
                                            variant="ghost" 
                                            size="sm"
                                            onClick={() => removeItem(index)}
                                            className="text-red-400 hover:text-red-300"
                                        >
                                            <XCircle className="h-4 w-4" />
                                        </Button>
                                    </div>
                                ))}
                            </div>

                            {/* Fees */}
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <label className="text-sm font-medium">Delivery Fee</label>
                                    <input
                                        type="number"
                                        value={newOrder.delivery_fee}
                                        onChange={(e) => {
                                            setNewOrder({...newOrder, delivery_fee: parseFloat(e.target.value) || 0});
                                            calculateTotals();
                                        }}
                                        className="w-full bg-background border border-input rounded-lg px-3 py-2"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-sm font-medium">Platform Fee</label>
                                    <input
                                        type="number"
                                        value={newOrder.platform_fee}
                                        onChange={(e) => {
                                            setNewOrder({...newOrder, platform_fee: parseFloat(e.target.value) || 0});
                                            calculateTotals();
                                        }}
                                        className="w-full bg-background border border-input rounded-lg px-3 py-2"
                                    />
                                </div>
                            </div>

                            {/* Totals */}
                            <div className="flex justify-between items-center pt-4 border-t border-border">
                                <div className="text-sm text-muted-foreground">
                                    Subtotal: ${newOrder.subtotal.toFixed(2)} | Tax: ${newOrder.tax.toFixed(2)}
                                </div>
                                <div className="text-xl font-bold">
                                    Total: ${newOrder.total.toFixed(2)}
                                </div>
                            </div>

                            {/* Instructions */}
                            <textarea
                                placeholder="Special instructions..."
                                value={newOrder.special_instructions}
                                onChange={(e) => setNewOrder({...newOrder, special_instructions: e.target.value})}
                                className="w-full bg-background border border-input rounded-lg px-3 py-2 h-20 resize-none"
                            />
                        </CardContent>
                        <div className="p-6 pt-0 flex justify-end gap-3">
                            <Button variant="outline" onClick={() => setShowNewOrderModal(false)}>
                                Cancel
                            </Button>
                            <Button onClick={handleCreateOrder}>
                                Create Order
                            </Button>
                        </div>
                    </Card>
                </div>
            )}
        </Layout>
    );
};

export default DeliveryHub;
